import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Button,
} from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import {
  formatForAPI,
  formatForFlatpickr,
} from '../../../../utils/dateformatpickr';
import { CRUDE_DOC_ACCEPT } from './registrationConstants';

// Shared "Add Document" popup used by both the Evaluation tab and the
// Implementation tab (rule-crude-registration.md §Tab Evaluation 2.1 / §Tab
// Implementation b.1). Collects Reference Document (text), Effective Date
// (date picker) and a file (PDF), then hands them to the parent's `onSave`.
// The parent only STAGES the entry locally (no network call here) — the actual
// upload is batch-flushed later at the tab boundary — so `onSave` is synchronous
// and this component stays presentational and reusable.
const AddDocumentModal = ({ isOpen, toggle, onSave }) => {
  const [refDocument, setRefDocument] = useState('');
  const [effDate, setEffDate] = useState(null);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const reset = () => {
    setRefDocument('');
    setEffDate(null);
    setFile(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Clear the form whenever the modal is (re)opened so a previous entry doesn't
  // leak into the next section's Add.
  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen]);

  const handleFileChange = e => {
    const f = e.target.files[0] || null;
    // Draft document flow accepts .pdf and .cru (api-crude-registration.md §D4).
    if (f && !/\.(pdf|cru)$/i.test(f.name)) {
      setFile(null);
      setErrors(prev => ({
        ...prev,
        file: 'Only .pdf or .cru files are allowed',
      }));
      return;
    }
    setFile(f);
    setErrors(prev => ({ ...prev, file: '' }));
  };

  const handleSave = () => {
    const newErrors = {};
    if (!refDocument.trim())
      newErrors.refDocument = 'Reference Document is required';
    if (!effDate) newErrors.effDate = 'Effective Date is required';
    if (!file) newErrors.file = 'File is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSave({
      refDocument: refDocument.trim(),
      effDate: formatForAPI(effDate),
      file,
    });
  };

  const handleCancel = () => {
    reset();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      data-bs-backdrop='static'
    >
      <ModalHeader toggle={handleCancel}>Add Document</ModalHeader>
      <ModalBody>
        <div className='mb-3'>
          <Label className='form-label'>
            Reference Document <span className='text-danger'>*</span>
          </Label>
          <Input
            type='text'
            value={refDocument}
            invalid={!!errors.refDocument}
            onChange={e => {
              setRefDocument(e.target.value);
              setErrors(prev => ({ ...prev, refDocument: '' }));
            }}
          />
          {errors.refDocument && (
            <div className='invalid-feedback d-block'>{errors.refDocument}</div>
          )}
        </div>

        <div className='mb-3'>
          <Label className='form-label'>
            Effective Date <span className='text-danger'>*</span>
          </Label>
          <Flatpickr
            className={`form-control ${errors.effDate ? 'is-invalid' : ''}`}
            value={formatForFlatpickr(effDate)}
            options={{ dateFormat: 'd M, Y' }}
            onChange={([d]) => {
              setEffDate(d || null);
              setErrors(prev => ({ ...prev, effDate: '' }));
            }}
          />
          {errors.effDate && (
            <div className='invalid-feedback d-block'>{errors.effDate}</div>
          )}
        </div>

        <div className='mb-1'>
          <Label className='form-label'>
            File <span className='text-danger'>*</span>
          </Label>
          <Input
            innerRef={fileInputRef}
            type='file'
            accept={CRUDE_DOC_ACCEPT}
            invalid={!!errors.file}
            onChange={handleFileChange}
          />
          <div className='form-text'>Supported formats: .pdf, .cru</div>
          {errors.file && (
            <div className='invalid-feedback d-block'>{errors.file}</div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={handleCancel}>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSave}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddDocumentModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  onSave: PropTypes.func,
};

export default AddDocumentModal;
